/**
 * Communications & Marketing Engine — Message Management Page
 * 
 * CRUD interface for managing communication messages per organization.
 * Supports tag/group/global scoping, priority levels, emergency mode,
 * image display modes, scheduling, and preview-before-publish.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Edit, Trash2, Eye, Loader2, Save,
  MessageSquare, AlertTriangle, Clock, Tag, Globe, Users,
  Image as ImageIcon, Link2, DollarSign, Megaphone,
} from 'lucide-react';
import brandConfig from '../../config/brand';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_COLORS = {
  low: 'bg-gray-500/20 text-gray-400',
  normal: 'bg-blue-500/20 text-blue-400',
  high: 'bg-orange-500/20 text-orange-400',
  critical: 'bg-red-500/20 text-red-400',
};

const SCOPE_ICONS = { tag: Tag, group: Users, global: Globe };

const EMPTY_MSG = {
  title: '', description: '', scope: 'global', scope_tag_ids: [],
  status: 'active', start_date: '', end_date: '',
  image_display_mode: 'no_image', custom_image_url: '',
  price: '', link_url: '', priority: 'normal', is_emergency: false,
};

export default function CommunicationsMessages() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [config, setConfig] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewMsg, setPreviewMsg] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_MSG });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchData = useCallback(async () => {
    try {
      const [msgRes, cfgRes, cardRes] = await Promise.all([
        fetch(`${API}/api/communications/messages/${orgId}`, { headers }),
        fetch(`${API}/api/communications/config/${orgId}`, { headers }),
        fetch(`${API}/api/hotels-standalone/hotels/${orgId}/cards`, { headers }),
      ]);
      const msgData = await msgRes.json();
      setMessages(Array.isArray(msgData) ? msgData : []);
      setConfig(await cfgRes.json());
      const cardData = await cardRes.json();
      setCards(Array.isArray(cardData) ? cardData : cardData.cards || []);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [orgId, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setForm({ ...EMPTY_MSG, scope: config?.default_message_scope || 'global' });
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEdit = (msg) => {
    setForm({
      title: msg.title || '', description: msg.description || '',
      scope: msg.scope || 'global', scope_tag_ids: msg.scope_tag_ids || [],
      status: msg.status || 'active',
      start_date: msg.start_date ? msg.start_date.slice(0, 16) : '',
      end_date: msg.end_date ? msg.end_date.slice(0, 16) : '',
      image_display_mode: msg.image_display_mode || 'no_image',
      custom_image_url: msg.custom_image_url || '',
      price: msg.price || '', link_url: msg.link_url || '',
      priority: msg.priority || 'normal', is_emergency: msg.is_emergency || false,
    });
    setEditingId(msg.id);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
        end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
        price: form.price || null,
      };
      const url = editingId
        ? `${API}/api/communications/messages/${orgId}/${editingId}`
        : `${API}/api/communications/messages/${orgId}`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST', headers, body: JSON.stringify(payload),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Save failed'); }
      toast.success(editingId ? 'Message updated' : 'Message created');
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await fetch(`${API}/api/communications/messages/${orgId}/${id}`, { method: 'DELETE', headers });
      toast.success('Message deleted');
      fetchData();
    } catch { toast.error('Delete failed'); }
  };

  const openPreview = (msg) => { setPreviewMsg(msg); setPreviewOpen(true); };

  if (loading) return (
    <div className="min-h-screen bg-[hsl(220,21%,7%)] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
    </div>
  );

  const activeCount = messages.filter(m => m.status === 'active').length;

  return (
    <div className="min-h-screen bg-[hsl(220,21%,7%)] text-white">
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-teal-400" />
                Communications & Marketing
              </h1>
              <p className="text-sm text-[hsl(215,20%,55%)]">
                {activeCount} active message{activeCount !== 1 ? 's' : ''} of {config?.max_active_messages || 10} max
              </p>
            </div>
          </div>
          <Button onClick={openCreate} data-testid="create-message-btn" style={{ backgroundColor: brandConfig.primaryColor }}>
            <Plus className="w-4 h-4 mr-2" /> New Message
          </Button>
        </div>

        {/* Messages List */}
        {messages.length === 0 ? (
          <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,22%)]">
            <CardContent className="py-12 text-center">
              <MessageSquare className="w-12 h-12 text-[hsl(215,20%,35%)] mx-auto mb-3" />
              <p className="text-[hsl(215,20%,55%)]">No messages yet. Create your first communication.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {messages.map(msg => {
              const ScopeIcon = SCOPE_ICONS[msg.scope] || Globe;
              return (
                <Card key={msg.id} className={`bg-[hsl(217,19%,12%)] border-[hsl(217,19%,22%)] ${msg.is_emergency ? 'border-red-500/50' : ''}`} data-testid={`message-card-${msg.id}`}>
                  <CardContent className="py-4 px-5">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {msg.is_emergency && (
                            <span className="px-2 py-0.5 rounded text-xs bg-red-600 text-white font-medium">EMERGENCY</span>
                          )}
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${PRIORITY_COLORS[msg.priority] || PRIORITY_COLORS.normal}`}>
                            {msg.priority?.toUpperCase()}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs ${msg.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                            {msg.status}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-[hsl(215,20%,50%)]">
                            <ScopeIcon className="w-3 h-3" /> {msg.scope}
                          </span>
                        </div>
                        <h3 className="text-white font-semibold truncate">{msg.title}</h3>
                        {msg.description && <p className="text-sm text-[hsl(215,20%,55%)] mt-1 line-clamp-2">{msg.description}</p>}
                        <div className="flex items-center gap-4 mt-2 text-xs text-[hsl(215,20%,45%)]">
                          {msg.start_date && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(msg.start_date).toLocaleDateString()}</span>}
                          {msg.end_date && <span>→ {new Date(msg.end_date).toLocaleDateString()}</span>}
                          {msg.price && <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />{msg.price}</span>}
                          {msg.link_url && <span className="flex items-center gap-1"><Link2 className="w-3 h-3" />Link</span>}
                          {msg.image_display_mode !== 'no_image' && <span className="flex items-center gap-1"><ImageIcon className="w-3 h-3" />Image</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-3">
                        <Button variant="ghost" size="sm" onClick={() => openPreview(msg)} data-testid={`preview-msg-${msg.id}`}><Eye className="w-4 h-4 text-teal-400" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(msg)} data-testid={`edit-msg-${msg.id}`}><Edit className="w-4 h-4 text-blue-400" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(msg.id)} data-testid={`delete-msg-${msg.id}`}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)] text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Message' : 'New Message'}</DialogTitle>
            <DialogDescription className="text-[hsl(215,20%,55%)]">
              {editingId ? 'Update communication details' : 'Create a new communication for your tags'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Title */}
            <div className="space-y-1.5">
              <Label className="text-[hsl(215,20%,75%)]">Message Title *</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="e.g., Weekend Special Offer" className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white" data-testid="msg-title-input" />
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <Label className="text-[hsl(215,20%,75%)]">Description</Label>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                rows={3} placeholder="Message details..."
                className="w-full rounded-md bg-[hsl(217,19%,17%)] border border-[hsl(217,19%,27%)] text-white px-3 py-2 text-sm" data-testid="msg-desc-input" />
            </div>

            {/* Priority + Emergency */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[hsl(215,20%,75%)]">Priority Level</Label>
                <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white" data-testid="msg-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[hsl(215,20%,75%)]">Emergency Mode</Label>
                <div className="flex items-center space-x-3 h-10 px-3 rounded-md bg-[hsl(217,19%,17%)] border border-[hsl(217,19%,27%)]">
                  <Switch checked={form.is_emergency} onCheckedChange={v => setForm({ ...form, is_emergency: v })} data-testid="msg-emergency-toggle" />
                  <span className={`text-sm ${form.is_emergency ? 'text-red-400' : 'text-[hsl(215,20%,55%)]'}`}>
                    {form.is_emergency ? 'EMERGENCY ON' : 'Off'}
                  </span>
                </div>
              </div>
            </div>

            {/* Scope */}
            <div className="space-y-1.5">
              <Label className="text-[hsl(215,20%,75%)]">Scope</Label>
              <Select value={form.scope} onValueChange={v => setForm({ ...form, scope: v })}>
                <SelectTrigger className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white" data-testid="msg-scope-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tag">This Tag Only</SelectItem>
                  <SelectItem value="group">Selected Tags (Group)</SelectItem>
                  <SelectItem value="global">All Tags (Global)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Tag selector for tag/group scope */}
            {(form.scope === 'tag' || form.scope === 'group') && (
              <div className="space-y-1.5">
                <Label className="text-[hsl(215,20%,75%)]">Select Tags</Label>
                <div className="max-h-32 overflow-y-auto rounded-md bg-[hsl(217,19%,17%)] border border-[hsl(217,19%,27%)] p-2 space-y-1">
                  {cards.length === 0 ? (
                    <p className="text-xs text-[hsl(215,20%,45%)] py-2 text-center">No tags available</p>
                  ) : cards.map(c => (
                    <label key={c.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-[hsl(217,19%,22%)] cursor-pointer">
                      <input type="checkbox" checked={form.scope_tag_ids.includes(c.id)}
                        onChange={e => {
                          const ids = e.target.checked
                            ? [...form.scope_tag_ids, c.id]
                            : form.scope_tag_ids.filter(id => id !== c.id);
                          setForm({ ...form, scope_tag_ids: ids });
                        }}
                        className="rounded" />
                      <span className="text-sm text-white">{c.card_id_display || c.id}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Scheduling */}
            {config?.allow_scheduling !== false && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[hsl(215,20%,75%)]">Start Date</Label>
                  <Input type="datetime-local" value={form.start_date}
                    onChange={e => setForm({ ...form, start_date: e.target.value })}
                    className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[hsl(215,20%,75%)]">End Date {config?.require_expiration && '*'}</Label>
                  <Input type="datetime-local" value={form.end_date}
                    onChange={e => setForm({ ...form, end_date: e.target.value })}
                    className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white" />
                </div>
              </div>
            )}

            {/* Image Display Mode */}
            {config?.allow_images !== false && (
              <div className="space-y-1.5">
                <Label className="text-[hsl(215,20%,75%)]">Image Display</Label>
                <Select value={form.image_display_mode} onValueChange={v => setForm({ ...form, image_display_mode: v })}>
                  <SelectTrigger className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no_image">No Image</SelectItem>
                    <SelectItem value="company_logo">Use Company Logo</SelectItem>
                    <SelectItem value="custom_image">Use Custom Image</SelectItem>
                  </SelectContent>
                </Select>
                {form.image_display_mode === 'custom_image' && (
                  <Input value={form.custom_image_url} onChange={e => setForm({ ...form, custom_image_url: e.target.value })}
                    placeholder="https://example.com/image.jpg" className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white mt-2" />
                )}
              </div>
            )}

            {/* Optional: Price + Link */}
            <div className="grid grid-cols-2 gap-3">
              {config?.allow_price_field && (
                <div className="space-y-1.5">
                  <Label className="text-[hsl(215,20%,75%)]">Price</Label>
                  <Input value={form.price} onChange={e => setForm({ ...form, price: e.target.value })}
                    placeholder="$19.99" className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white" />
                </div>
              )}
              {config?.allow_link_url !== false && (
                <div className="space-y-1.5">
                  <Label className="text-[hsl(215,20%,75%)]">Link / URL</Label>
                  <Input value={form.link_url} onChange={e => setForm({ ...form, link_url: e.target.value })}
                    placeholder="https://..." className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white" />
                </div>
              )}
            </div>

            {/* Status */}
            <div className="space-y-1.5">
              <Label className="text-[hsl(215,20%,75%)]">Status</Label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setPreviewMsg({ ...form }); setPreviewOpen(true); }} className="border-[hsl(217,19%,27%)] text-[hsl(215,20%,75%)]">
              <Eye className="w-4 h-4 mr-2" /> Preview
            </Button>
            <Button onClick={handleSave} disabled={saving} style={{ backgroundColor: brandConfig.primaryColor }} data-testid="save-message-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {editingId ? 'Update' : 'Publish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-white text-gray-900 max-w-sm p-0 rounded-2xl overflow-hidden">
          <div className="bg-gradient-to-b from-teal-500 to-teal-600 px-5 py-4 text-white">
            <p className="text-xs uppercase tracking-wider opacity-75 mb-1">Preview — End User View</p>
            <h2 className="text-lg font-bold">{previewMsg?.title || 'Untitled'}</h2>
          </div>
          <div className="px-5 py-4 space-y-3">
            {previewMsg?.resolved_image_url || (previewMsg?.image_display_mode === 'custom_image' && previewMsg?.custom_image_url) ? (
              <img src={previewMsg.resolved_image_url || previewMsg.custom_image_url} alt="" className="w-full h-40 object-cover rounded-lg" />
            ) : null}
            {previewMsg?.description && <p className="text-gray-700 text-sm">{previewMsg.description}</p>}
            {previewMsg?.price && <p className="text-xl font-bold text-teal-600">{previewMsg.price}</p>}
            {previewMsg?.link_url && (
              <a href={previewMsg.link_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-teal-600 text-sm font-medium hover:underline">
                <Link2 className="w-4 h-4" /> Learn More
              </a>
            )}
            {previewMsg?.is_emergency && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <span className="text-red-700 text-sm font-medium">Emergency Message</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
